import {
  type DocumentData,
  type FirestoreDataConverter,
  type PartialWithFieldValue,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type WithFieldValue,
  collection,
  doc,
  type Firestore,
} from 'firebase/firestore';

import type {
  Appointment,
  Balance,
  Business,
  Client,
  OwnerType,
  Professional,
  Product,
  ProductWithId,
  Public,
  Sale,
  Service,
  Settlement,
  Slug,
  Transaction,
} from '../schema';
import type { Tasa } from '../schema/settings/tasa-schema';
import type {
  LegalVersion,
  UserLegalAcceptance,
  AdminSettings,
  AdminPrivate,
  BillingRecord,
  MarketingConfig,
  MarketingThread,
  PaymentMethod,

  LandingMetrics,
  GlobalAdmin,
  Employee,
  PayrollCycle,
} from '../schema';
import { FirestorePaths } from './firestore-paths';


// Type for global payment methods document
export interface GlobalPaymentMethodsDoc {
  methods: PaymentMethod[];
  updatedAt?: any; // Firestore timestamp
}

/**
 * CONVERTER GENÉRICO SEGURO
 * T: El tipo de los datos en Firestore (sin ID).
 * Retorna T & { id: string } para el frontend.
 */
export const createConverter = <T extends object>(): FirestoreDataConverter<
  T & { id: string },
  DocumentData
> => ({
  // Permitimos que el objeto de entrada sea T (sin ID) o T con un ID opcional
  toFirestore: (
    modelObject:
      | WithFieldValue<T>
      | PartialWithFieldValue<T>
      | (T & { id?: string }),
  ): DocumentData => {
    // Si el objeto tiene un ID (por ejemplo en un update), lo sacamos para no guardarlo como campo
    const { id, ...data } = modelObject as any;
    return data as DocumentData;
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): T & { id: string } => {
    const data = snapshot.data(options) as T;
    return {
      ...data,
      id: snapshot.id,
    };
  },
});

/**
 * The Factory Function
 * Takes a Firestore instance and returns an object containing all unified references.
 */
export function createFirestoreRefs(db: Firestore) {
  return {
    // Utilities
    fieldToFilter: (ownerType: OwnerType) =>
      ownerType === 'business' ? 'businessId' : 'professionalId',

    // --- NESTED RESOURCES ---
    employeesCol: (businessId: string) =>
      collection(db, FirestorePaths.employees(businessId)).withConverter(
        createConverter<Employee>(),
      ),
    employeeRef: (businessId: string, employeeId: string) =>
      doc(db, FirestorePaths.employeeDoc(businessId, employeeId)).withConverter(
        createConverter<Employee>(),
      ),

    payrollCyclesCol: (businessId: string) =>
      collection(db, FirestorePaths.payroll_cycles(businessId)).withConverter(
        createConverter<PayrollCycle>(),
      ),
    payrollCycleRef: (businessId: string, cycleId: string) =>
      doc(db, FirestorePaths.payrollCycleDoc(businessId, cycleId)).withConverter(
        createConverter<PayrollCycle>(),
      ),

    // Global
    tasaDoc: () => doc(db, FirestorePaths.tasaDoc).withConverter(createConverter<Tasa>()),
    publicRef: (businessId: string) =>
      doc(db, FirestorePaths.publicDoc(businessId)).withConverter(createConverter<Public>()),

    // Config (now under admin)
    marketingConfigRef: (businessId: string) =>
      doc(db, FirestorePaths.marketingConfig(businessId)).withConverter(
        createConverter<MarketingConfig>(),
      ),

    // Slugs
    slugRef: (slug: string) =>
      doc(db, FirestorePaths.slugDoc(slug)).withConverter(createConverter<Slug>()),
    slugsCol: () => collection(db, FirestorePaths.slugs).withConverter(createConverter<Slug>()),

    // Businesses (Formerly Owners)
    businessRef: (businessId: string) =>
      doc(db, FirestorePaths.businessDoc(businessId)).withConverter(
        createConverter<Professional | Business>(),
      ),
    businessesCol: () =>
      collection(db, FirestorePaths.businesses).withConverter(
        createConverter<Professional | Business>(),
      ),

    // Compatibility aliases
    ownerRef: (businessId: string) =>
      doc(db, FirestorePaths.businessDoc(businessId)).withConverter(
        createConverter<Professional | Business>(),
      ),
    ownersCol: () =>
      collection(db, FirestorePaths.businesses).withConverter(
        createConverter<Professional | Business>(),
      ),

    // Appointments
    appointmentRef: (id: string) =>
      doc(db, FirestorePaths.appointmentDoc(id)).withConverter(createConverter<Appointment>()),
    appointmentsCol: () =>
      collection(db, FirestorePaths.appointments).withConverter(createConverter<Appointment>()),

    // Sales
    saleRef: (id: string) =>
      doc(db, FirestorePaths.saleDoc(id)).withConverter(createConverter<Sale>()),
    salesCol: () => collection(db, FirestorePaths.sales).withConverter(createConverter<Sale>()),

    // Clients
    clientsCol: (businessId: string) =>
      collection(db, FirestorePaths.clients(businessId)).withConverter(
        createConverter<Client>(),
      ),
    clientDoc: (businessId: string, clientId: string) =>
      doc(db, FirestorePaths.clientDoc(businessId, clientId)).withConverter(
        createConverter<Client>(),
      ),

    // Services
    servicesCol: () =>
      collection(db, FirestorePaths.services).withConverter(createConverter<Service>()),
    serviceDoc: (serviceId: string) =>
      doc(db, FirestorePaths.serviceDoc(serviceId)).withConverter(createConverter<Service>()),

    // Transactions
    transactionsCol: (businessId: string) =>
      collection(db, FirestorePaths.transactions(businessId)).withConverter(
        createConverter<Transaction>(),
      ),
    transactionRef: (businessId: string, transactionId: string) =>
      doc(db, FirestorePaths.transactionDoc(businessId, transactionId)).withConverter(
        createConverter<Transaction>(),
      ),

    // Balances
    balancesCol: (businessId: string) =>
      collection(db, FirestorePaths.balances(businessId)).withConverter(
        createConverter<Balance>(),
      ),
    balanceDoc: (businessId: string, workerId: string) =>
      doc(db, FirestorePaths.balanceDoc(businessId, workerId)).withConverter(
        createConverter<Balance>(),
      ),

    // Admin Specific
    adminSettingsRef: (businessId: string) =>
      doc(db, FirestorePaths.adminSettings(businessId)).withConverter(
        createConverter<AdminSettings>(),
      ),
    adminPrivateRef: (businessId: string) =>
      doc(db, FirestorePaths.adminPrivate(businessId)).withConverter(
        createConverter<AdminPrivate>(),
      ),
    adminBillingCol: (businessId: string) =>
      collection(db, FirestorePaths.adminBilling(businessId)).withConverter(
        createConverter<BillingRecord>(),
      ),

    // Global Payment Methods (Single document containing array of methods)
    // Editable by admin, read-only for users
    globalPaymentMethodsDoc: () =>
      doc(db, FirestorePaths.globalPaymentMethods).withConverter(
        createConverter<GlobalPaymentMethodsDoc>(),
      ),

    // Legal Documents
    legalVersionsCol: () =>
      collection(db, FirestorePaths.legalVersions).withConverter(
        createConverter<LegalVersion>(),
      ),
    userLegalAcceptancesCol: () =>
      collection(db, FirestorePaths.legalAcceptances).withConverter(
        createConverter<UserLegalAcceptance>(),
      ),

    // Settlements
    settlementsCol: (businessId: string) =>
      collection(db, FirestorePaths.settlements(businessId)).withConverter(
        createConverter<Settlement>(),
      ),
    settlementRef: (businessId: string, settlementId: string) =>
      doc(db, FirestorePaths.settlementDoc(businessId, settlementId)).withConverter(
        createConverter<Settlement>(),
      ),

    // Expenses
    expensesCol: (businessId: string) =>
      collection(db, FirestorePaths.expenses(businessId)),

    // Products
    productsCol: (businessId: string) =>
      collection(db, FirestorePaths.products(businessId)).withConverter(
        createConverter<Product>(),
      ),
    productRef: (businessId: string, productId: string) =>
      doc(db, FirestorePaths.productDoc(businessId, productId)).withConverter(
        createConverter<Product>(),
      ),

    // Metrics & Featured
    landingMetricsDoc: () =>
      doc(db, FirestorePaths.landingMetrics).withConverter(createConverter<LandingMetrics>()),

    // Meta Ads Global Mapping (Now part of Global Admin)
    globalAdminDoc: () =>
      doc(db, FirestorePaths.globalAdmin).withConverter(createConverter<GlobalAdmin>()),

    // --- MARKETING & AI ---
    marketingThreadsCol: (businessId: string) =>
      collection(db, FirestorePaths.marketingThreads(businessId)).withConverter(
        createConverter<MarketingThread>(),
      ),
    marketingThreadRef: (businessId: string, phone: string) =>
      doc(db, FirestorePaths.marketingThreadDoc(businessId, phone)).withConverter(
        createConverter<MarketingThread>(),
      ),
    marketingThreadMessagesCol: (businessId: string, phone: string) =>
      collection(db, FirestorePaths.marketingThreadMessages(businessId, phone)),
  };
}

